// Helper to convert ArrayBuffer to Base64
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
}

// Helper to convert Base64 to ArrayBuffer
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}

// Convert SPKI public key ArrayBuffer to standard PEM format
export function spkiToPem(spkiBuffer: ArrayBuffer): string {
    const b64 = arrayBufferToBase64(spkiBuffer);
    // Wrap to 64 char lines
    const formatted = b64.match(/.{1,64}/g)?.join('\n') || b64;
    return `-----BEGIN PUBLIC KEY-----\n${formatted}\n-----END PUBLIC KEY-----`;
}

// Convert PEM format back to SPKI public key ArrayBuffer
export function pemToSpki(pem: string): ArrayBuffer {
    const cleaned = pem
        .replace(/-----BEGIN PUBLIC KEY-----/, '')
        .replace(/-----END PUBLIC KEY-----/, '')
        .replace(/-----BEGIN PRIVATE KEY-----/, '')
        .replace(/-----END PRIVATE KEY-----/, '')
        .replace(/\s/g, '');
    return base64ToArrayBuffer(cleaned);
}

// 1. Generate NIST P-256 ECDH Keypair
export async function generateVaultKeypair(): Promise<{ publicKeyPem: string; privateKeyBase64: string }> {
    const keypair = await window.crypto.subtle.generateKey(
        {
            name: 'ECDH',
            namedCurve: 'P-256',
        },
        true, // extractable
        ['deriveKey', 'deriveBits']
    );

    const spki = await window.crypto.subtle.exportKey('spki', keypair.publicKey);
    const pkcs8 = await window.crypto.subtle.exportKey('pkcs8', keypair.privateKey);

    return {
        publicKeyPem: spkiToPem(spki),
        privateKeyBase64: arrayBufferToBase64(pkcs8),
    };
}

// 2. Derive AES-256 Wrapping Key using PBKDF2
export async function deriveWrappingKey(password: string, salt: string): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const passwordBytes = encoder.encode(password);
    const saltBytes = encoder.encode(salt);

    const baseKey = await window.crypto.subtle.importKey(
        'raw',
        passwordBytes,
        { name: 'PBKDF2' },
        false,
        ['deriveKey']
    );

    return await window.crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: saltBytes,
            iterations: 600000,
            hash: 'SHA-256',
        },
        baseKey,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
    );
}

// 3. Wrap (Encrypt) Vault Private Key using AES-256-GCM
export async function wrapPrivateKey(
    privateKeyBase64: string,
    wrappingKey: CryptoKey
): Promise<{ ciphertextBase64: string; nonceBase64: string }> {
    const privateKeyBuffer = base64ToArrayBuffer(privateKeyBase64);
    const nonce = window.crypto.getRandomValues(new Uint8Array(12));

    const ciphertext = await window.crypto.subtle.encrypt(
        {
            name: 'AES-GCM',
            iv: nonce,
        },
        wrappingKey,
        privateKeyBuffer
    );

    return {
        ciphertextBase64: arrayBufferToBase64(ciphertext),
        nonceBase64: arrayBufferToBase64(nonce),
    };
}

// 4. Unwrap (Decrypt) Vault Private Key using AES-256-GCM
export async function unwrapPrivateKey(
    wrappedPrivateKeyBase64: string,
    nonceBase64: string,
    wrappingKey: CryptoKey
): Promise<string> {
    const ciphertext = base64ToArrayBuffer(wrappedPrivateKeyBase64);
    const nonce = base64ToArrayBuffer(nonceBase64);

    const decryptedBuffer = await window.crypto.subtle.decrypt(
        {
            name: 'AES-GCM',
            iv: new Uint8Array(nonce),
        },
        wrappingKey,
        ciphertext
    );

    return arrayBufferToBase64(decryptedBuffer);
}

// 5. Encrypt password with Vault Public Key using P-256 ECDH + AES-256-GCM
export async function encryptPasswordWithPublicKey(
    password: string,
    publicKeyPem: string
): Promise<{ ephemeralPkPem: string; ciphertextBase64: string; nonceBase64: string }> {
    const spkiBuffer = pemToSpki(publicKeyPem);

    // Import Vault Public Key
    const vaultPubKey = await window.crypto.subtle.importKey(
        'spki',
        spkiBuffer,
        {
            name: 'ECDH',
            namedCurve: 'P-256',
        },
        true,
        []
    );

    // Generate Ephemeral Keypair
    const ephemeralKeypair = await window.crypto.subtle.generateKey(
        {
            name: 'ECDH',
            namedCurve: 'P-256',
        },
        true,
        ['deriveKey']
    );

    // Derive Shared Secret Key
    const aesKey = await window.crypto.subtle.deriveKey(
        {
            name: 'ECDH',
            public: vaultPubKey,
        },
        ephemeralKeypair.privateKey,
        {
            name: 'AES-GCM',
            length: 256,
        },
        false,
        ['encrypt']
    );

    // Encrypt password
    const nonce = window.crypto.getRandomValues(new Uint8Array(12));
    const plaintextBytes = new TextEncoder().encode(password);
    const ciphertext = await window.crypto.subtle.encrypt(
        {
            name: 'AES-GCM',
            iv: nonce,
        },
        aesKey,
        plaintextBytes
    );

    const ephemeralSpki = await window.crypto.subtle.exportKey('spki', ephemeralKeypair.publicKey);

    return {
        ephemeralPkPem: spkiToPem(ephemeralSpki),
        ciphertextBase64: arrayBufferToBase64(ciphertext),
        nonceBase64: arrayBufferToBase64(nonce),
    };
}

// 6. Decrypt password using Vault Private Key and Ephemeral Public Key
export async function decryptPassword(
    ciphertextBase64: string,
    nonceBase64: string,
    ephemeralPkPem: string,
    vaultPrivateKeyBase64: string
): Promise<string> {
    const ciphertext = base64ToArrayBuffer(ciphertextBase64);
    const nonce = base64ToArrayBuffer(nonceBase64);
    const vaultPkBuffer = base64ToArrayBuffer(vaultPrivateKeyBase64);
    const ephemeralSpkiBuffer = pemToSpki(ephemeralPkPem); // Convert standard PEM to SPKI buffer

    // Import Vault Private Key (PKCS8)
    const vaultPrivKey = await window.crypto.subtle.importKey(
        'pkcs8',
        vaultPkBuffer,
        {
            name: 'ECDH',
            namedCurve: 'P-256',
        },
        true,
        ['deriveKey']
    );

    // Import Ephemeral Public Key (SPKI)
    const ephemeralPubKey = await window.crypto.subtle.importKey(
        'spki',
        ephemeralSpkiBuffer,
        {
            name: 'ECDH',
            namedCurve: 'P-256',
        },
        true,
        []
    );

    // Derive Shared Secret Key
    const aesKey = await window.crypto.subtle.deriveKey(
        {
            name: 'ECDH',
            public: ephemeralPubKey,
        },
        vaultPrivKey,
        {
            name: 'AES-GCM',
            length: 256,
        },
        false,
        ['decrypt']
    );

    // Decrypt
    const plaintextBuffer = await window.crypto.subtle.decrypt(
        {
            name: 'AES-GCM',
            iv: new Uint8Array(nonce),
        },
        aesKey,
        ciphertext
    );

    return new TextDecoder().decode(plaintextBuffer);
}

// 7. Unwrap (Decrypt) Vault Private Key using AES-256-GCM directly to non-extractable CryptoKey
export async function unwrapPrivateKeyToCryptoKey(
    wrappedPrivateKeyBase64: string,
    nonceBase64: string,
    wrappingKey: CryptoKey,
    extractable = false
): Promise<CryptoKey> {
    const ciphertext = base64ToArrayBuffer(wrappedPrivateKeyBase64);
    const nonce = base64ToArrayBuffer(nonceBase64);

    const decryptedBuffer = await window.crypto.subtle.decrypt(
        {
            name: 'AES-GCM',
            iv: new Uint8Array(nonce),
        },
        wrappingKey,
        ciphertext
    );

    return await window.crypto.subtle.importKey(
        'pkcs8',
        decryptedBuffer,
        {
            name: 'ECDH',
            namedCurve: 'P-256',
        },
        extractable,
        ['deriveKey']
    );
}

// 8. Decrypt password using Vault Private Key (CryptoKey) and Ephemeral Public Key
export async function decryptPasswordWithCryptoKey(
    ciphertextBase64: string,
    nonceBase64: string,
    ephemeralPkPem: string,
    vaultPrivKey: CryptoKey
): Promise<string> {
    const ciphertext = base64ToArrayBuffer(ciphertextBase64);
    const nonce = base64ToArrayBuffer(nonceBase64);
    const ephemeralSpkiBuffer = pemToSpki(ephemeralPkPem);

    // Import Ephemeral Public Key (SPKI)
    const ephemeralPubKey = await window.crypto.subtle.importKey(
        'spki',
        ephemeralSpkiBuffer,
        {
            name: 'ECDH',
            namedCurve: 'P-256',
        },
        true,
        []
    );

    // Derive Shared Secret Key
    const aesKey = await window.crypto.subtle.deriveKey(
        {
            name: 'ECDH',
            public: ephemeralPubKey,
        },
        vaultPrivKey,
        {
            name: 'AES-GCM',
            length: 256,
        },
        false,
        ['decrypt']
    );

    // Decrypt
    const plaintextBuffer = await window.crypto.subtle.decrypt(
        {
            name: 'AES-GCM',
            iv: new Uint8Array(nonce),
        },
        aesKey,
        ciphertext
    );

    return new TextDecoder().decode(plaintextBuffer);
}

